/**
 * Archivo: AjustesAdmin.js
 * Autor: Acher Egido Mairal
 * Fecha de última modificación: 2024-08-20
 * 
 * Descripción:
 * Este componente define la vista de los ajustes para un administrador, permitiendo realizar acciones críticas 
 * como borrar la base de datos, eliminar colegiales, mensajes, usuarios, etc.
 * 
 * Dependencias:
 * - react: ^18.2.0
 * - js-cookie: ^3.0.1
 * - Componentes/MetaTags: Componente para las etiquetas meta del documento.
 * - Componentes/Cabecera: Componente para la cabecera principal.
 * - Componentes/SegundaCabecera: Componente para la segunda cabecera de navegación.
 * - Servicios/AjustesServicios: Funciones para interactuar con el servicio de ajustes.
 */

import React from 'react';
import Cookies from 'js-cookie';
import '../Estilos/estilos.css';
import MetaTags from '../Componentes/MetaTags';
import Cabecera from '../Componentes/Cabecera';
import SegundaCabecera from '../Componentes/SegundaCabecera';
import { borrarBaseDeDatos, borrarColegiales, borrarMensajesGlobales, borrarComunicados, borrarMenus, borrarUsuarios, borrarBoletines } from '../Servicios/AjustesServicios';

function AjustesAdmin() {
    const token = Cookies.get('token');
    const permisos = Cookies.get('permisos');  // Asumiendo que se guarda en cookies

    const handleBorrarBaseDeDatos = async () => {
        if (window.confirm("¿Estás seguro de que quieres borrar toda la base de datos? Esta acción no se puede deshacer.")) {
            const result = await borrarBaseDeDatos(token);
            if (result.success) {
                alert("Base de datos borrada con éxito.");
            } else {
                alert("Error al borrar la base de datos: " + result.message);
            }
        }
    };

    const handleBorrarColegiales = async () => {
        if (window.confirm("¿Estás seguro de que quieres borrar todos los colegiales? Esta acción no se puede deshacer.")) {
            const result = await borrarColegiales(token);
            if (result.success) {
                alert("Colegiales borrados con éxito.");
            } else {
                alert("Error al borrar los colegiales: " + result.message);
            }
        }
    };

    const handleBorrarMensajesGlobales = async () => {
        const result = await borrarMensajesGlobales(token);
        if (result.success) {
            alert("Mensajes globales borrados con éxito.");
        } else {
            alert("Error al borrar los mensajes globales: " + result.message);
        }
    };

    const handleBorrarComunicados = async () => {
        const result = await borrarComunicados(token);
        if (result.success) {
            alert("Comunicados borrados con éxito.");
        } else {
            alert("Error al borrar los comunicados: " + result.message);
        }
    };

    const handleBorrarMenus = async () => {
        const result = await borrarMenus(token);
        if (result.success) {
            alert("Menús de comedor borrados con éxito.");
        } else {
            alert("Error al borrar los menús: " + result.message);
        }
    };

    const handleBorrarUsuarios = async () => {
        if (window.confirm("¿Estás seguro de que quieres borrar todos los usuarios? Esta acción no se puede deshacer.")) {
            const result = await borrarUsuarios(token);
            if (result.success) {
                alert("Usuarios borrados con éxito.");
            } else {
                alert("Error al borrar los usuarios: " + result.message);
            }
        }
    };

    const handleBorrarBoletines = async () => {
        const result = await borrarBoletines(token);
        if (result.success) {
            alert("Boletines semanales borrados con éxito.");
        } else {
            alert("Error al borrar los boletines: " + result.message);
        }
    };

    return (
        <>
            <MetaTags />
            <div className="fondo">
                <Cabecera permisos={permisos} />
                {/* Mostrar la segunda cabecera solo en pantallas grandes */}
                <div className="d-none d-md-block">
                    <SegundaCabecera hayVentanas={false} permisos={permisos} />
                </div>
                <div className='container-fluid d-flex p-0 mt-2' style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                    <div className='container-fluid col-12 col-md-11' style={{ opacity: 1 }}>
                        <div className='row-fluid d-flex justify-content-between p-2'>
                            <h3 className="text-black">Ajustes de Administrador</h3>
                        </div>
                        <div className="row">
                            <div className="col-12 mb-1">
                                <button className="btn btn-danger w-100" onClick={handleBorrarBaseDeDatos}>
                                    Borrar toda la base de datos
                                </button>
                            </div>
                            <div className='col-12 mb-2'>
                                <span className="customFontSizeTextoMediano">Se borrará toda la base de datos, menos el usuario administrador que hace la petición.</span>
                            </div>
                            <div className="col-12 mb-3">
                                <button className="btn btn-danger w-100" onClick={handleBorrarUsuarios}>
                                    Eliminar usuarios
                                </button>
                                <p className="customFontSizeTextoMediano">Se eliminarán todos los usuarios menos el usuario administrador que hace la petición.</p>
                            </div>
                            <div className="col-12 mb-3">
                                <button className="btn w-100" onClick={handleBorrarColegiales}>
                                    Borrar todos los colegiales
                                </button>
                                <p className="customFontSizeTextoMediano">Se borrarán las cuentas de usuario asociadas a estos colegiales.</p>
                            </div>
                            <div className="col-12 mb-3">
                                <button className="btn w-100" onClick={handleBorrarMensajesGlobales}>
                                    Eliminar mensajes globales
                                </button>
                            </div>
                            <div className="col-12 mb-3">
                                <button className="btn w-100" onClick={handleBorrarComunicados}>
                                    Eliminar comunicados oficiales
                                </button>
                            </div>
                            <div className="col-12 mb-3">
                                <button className="btn  w-100" onClick={handleBorrarMenus}>
                                    Eliminar menús de comedor
                                </button>
                            </div>
                            <div className="col-12 mb-3">
                                <button className="btn w-100" onClick={handleBorrarBoletines}>
                                    Eliminar boletines semanales
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default AjustesAdmin;
